// Lógica pura del módulo Construcción: avance ponderado, curva S, Earned Value
// Management, costo real desde Abastecimiento, conciliación de materiales y
// alertas. Sin React ni estado: todo se puede probar en evm.test.js.
//
// Convenciones:
// - Fechas como 'yyyy-MM-dd' (igual que el resto del ERP).
// - Montos de obra (BAC, contratos) en la moneda del proyecto de construcción.
// - Sólo las partidas hoja (sin hijos) llevan ponderación, BAC y avance; las
//   fases y partidas padre se calculan agregando a sus hijas.

const DAY_MS = 24 * 60 * 60 * 1000;

export const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const toIsoDate = (date) => {
  const d = toDate(date);
  if (!d) return null;
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

export const clampPct = (value) => Math.max(0, Math.min(100, Number(value) || 0));

const ratio = (numerator, denominator) => (denominator > 0 ? numerator / denominator : null);

// ---------------------------------------------------------------------------
// Estructura WBS
// ---------------------------------------------------------------------------

export const childrenByParent = (items = []) => {
  const map = new Map();
  items.forEach((item) => {
    const key = item.parentId || null;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  });
  map.forEach((list) => list.sort((a, b) => String(a.code || '').localeCompare(String(b.code || ''), 'es', { numeric: true })));
  return map;
};

export const leafItems = (items = []) => {
  const parents = new Set(items.map((item) => item.parentId).filter(Boolean));
  return items.filter((item) => !parents.has(item.id));
};

// Lista en orden de árbol (fase → partida → subpartida) con profundidad.
export const flattenTree = (items = []) => {
  const byParent = childrenByParent(items);
  const result = [];
  const walk = (parentId, depth) => {
    (byParent.get(parentId) || []).forEach((item) => {
      result.push({ ...item, depth });
      walk(item.id, depth + 1);
    });
  };
  walk(null, 0);
  return result;
};

const descendantLeaves = (itemId, byParent) => {
  const children = byParent.get(itemId) || [];
  if (children.length === 0) return [itemId];
  return children.flatMap((child) => descendantLeaves(child.id, byParent));
};

// ---------------------------------------------------------------------------
// Avance físico
// ---------------------------------------------------------------------------

// % planificado de una partida a una fecha: lineal entre inicio y fin de la
// línea base (0 antes del inicio, 100 después del término).
export const plannedPctAt = (item, asOf) => {
  const start = toDate(item?.baselineStart);
  const end = toDate(item?.baselineEnd);
  const date = toDate(asOf);
  if (!start || !end || !date) return 0;
  if (date < start) return 0;
  if (date >= end) return 100;
  const total = end.getTime() - start.getTime();
  if (total <= 0) return 100;
  return clampPct(((date.getTime() - start.getTime()) / total) * 100);
};

// Último avance aprobado registrado para la partida hasta la fecha indicada.
export const actualPctAt = (itemId, logs = [], asOf = null) => {
  const limit = asOf ? toDate(asOf) : null;
  let best = null;
  logs.forEach((log) => {
    if (log.wbsItemId !== itemId || log.status !== 'aprobado') return;
    const date = toDate(log.date);
    if (!date || (limit && date > limit)) return;
    if (!best || date > best.date || (date.getTime() === best.date.getTime() && (log.createdAt || '') > (best.createdAt || ''))) {
      best = { date, progress: log.progress, createdAt: log.createdAt };
    }
  });
  return best ? clampPct(best.progress) : 0;
};

// Peso efectivo de cada hoja. Si ninguna tiene ponderación explícita se usa la
// participación en el BAC; si tampoco hay BAC, todas pesan igual.
export const effectiveWeights = (items = []) => {
  const leaves = leafItems(items);
  const explicit = leaves.reduce((sum, leaf) => sum + (Number(leaf.weight) || 0), 0);
  const totalBac = leaves.reduce((sum, leaf) => sum + (Number(leaf.bac) || 0), 0);
  const weights = new Map();
  leaves.forEach((leaf) => {
    let weight;
    if (explicit > 0) weight = (Number(leaf.weight) || 0) / explicit;
    else if (totalBac > 0) weight = (Number(leaf.bac) || 0) / totalBac;
    else weight = leaves.length > 0 ? 1 / leaves.length : 0;
    weights.set(leaf.id, weight);
  });
  return weights;
};

export const projectProgressAt = (items = [], logs = [], asOf) => {
  const weights = effectiveWeights(items);
  let planned = 0;
  let actual = 0;
  leafItems(items).forEach((leaf) => {
    const weight = weights.get(leaf.id) || 0;
    planned += weight * plannedPctAt(leaf, asOf);
    actual += weight * actualPctAt(leaf.id, logs, asOf);
  });
  return {
    planned: clampPct(planned),
    actual: clampPct(actual),
    spi: ratio(actual, planned)
  };
};

// Curva S: avance programado vs real en puntos semanales. El real se corta en
// la fecha de corte (no se proyecta a futuro).
export const sCurve = (items = [], logs = [], { asOf, maxPoints = 40 } = {}) => {
  const leaves = leafItems(items);
  const starts = leaves.map((leaf) => toDate(leaf.baselineStart)).filter(Boolean);
  const ends = leaves.map((leaf) => toDate(leaf.baselineEnd)).filter(Boolean);
  if (starts.length === 0 || ends.length === 0) return [];
  const cutoff = toDate(asOf) || new Date();
  const first = new Date(Math.min(...starts.map((d) => d.getTime())));
  const last = new Date(Math.max(...ends.map((d) => d.getTime()), cutoff.getTime()));
  const spanDays = Math.max(1, Math.round((last - first) / DAY_MS));
  const stepDays = Math.max(7, Math.ceil(spanDays / maxPoints));
  const points = [];
  for (let offset = 0; offset <= spanDays + stepDays - 1; offset += stepDays) {
    const date = new Date(first.getTime() + Math.min(offset, spanDays) * DAY_MS);
    const { planned, actual } = projectProgressAt(items, logs, date);
    points.push({
      date: toIsoDate(date),
      planned: Math.round(planned * 10) / 10,
      actual: date <= cutoff ? Math.round(actual * 10) / 10 : null
    });
    if (offset >= spanDays) break;
  }
  return points;
};

// ---------------------------------------------------------------------------
// Costo real (AC) desde Abastecimiento y estados de pago
// ---------------------------------------------------------------------------

const EXCLUDED_ORDER_STATUSES = new Set(['rechazada', 'borrador', 'cancelada']);

// Costo real de una OC: lo facturado; si no hay factura pero ya se recibió,
// el valor recibido. Las OC sólo comprometidas (sin recepción) no son AC.
export const orderActualCost = (order) => {
  if (!order || EXCLUDED_ORDER_STATUSES.has(order.status)) return 0;
  const invoiced = Number(order.invoicedValue) || 0;
  if (invoiced > 0) return invoiced;
  const lifecycleReceived = Boolean(order.lifecycle?.goodsReceipt?.completed || order.lifecycle?.receivedAt);
  if (order.status === 'recibida' || lifecycleReceived) return Number(order.totalValue) || 0;
  return 0;
};

export const orderCommittedCost = (order) => {
  if (!order || EXCLUDED_ORDER_STATUSES.has(order.status)) return 0;
  return Number(order.totalValue) || 0;
};

// Reparte un monto entre partidas hoja en proporción a su BAC.
const distributeByBac = (amount, leafIds, items) => {
  const leaves = items.filter((item) => leafIds.includes(item.id));
  const totalBac = leaves.reduce((sum, leaf) => sum + (Number(leaf.bac) || 0), 0);
  const result = new Map();
  leaves.forEach((leaf) => {
    const share = totalBac > 0 ? (Number(leaf.bac) || 0) / totalBac : 1 / leaves.length;
    result.set(leaf.id, amount * share);
  });
  return result;
};

/**
 * AC del proyecto en su moneda. Fuentes:
 *  1. OC del parque imputadas al Área Construcción (o a su CECO). Si la OC trae
 *     paquete WBS, se asigna a las partidas enlazadas a ese paquete.
 *  2. Estados de pago aprobados/pagados de contratistas, repartidos entre las
 *     partidas del contrato. La OC enlazada a un contrato se excluye del punto 1
 *     para no contar dos veces el mismo subcontrato.
 */
export const computeActualCost = ({
  items = [],
  parkId,
  areaIds = [],
  costCenterIds = [],
  purchaseOrders = [],
  contractors = [],
  paymentStatements = [],
  convert = (amount) => amount,
  projectCurrency = 'USD'
}) => {
  const byItem = new Map();
  const add = (itemId, amount) => byItem.set(itemId, (byItem.get(itemId) || 0) + amount);
  const byParent = childrenByParent(items);
  const sources = [];
  let unassigned = 0;
  let committed = 0;

  const areaSet = new Set(areaIds);
  const ccSet = new Set(costCenterIds);
  const contractOrderIds = new Set(contractors.map((contract) => contract.purchaseOrderId).filter(Boolean));

  purchaseOrders.forEach((order) => {
    const inPark = order.parkId === parkId;
    const inConstruction = (order.areaId && areaSet.has(order.areaId)) || (order.costCenterId && ccSet.has(order.costCenterId));
    if (!inPark || !inConstruction || contractOrderIds.has(order.id)) return;
    const currency = order.currency || 'CLP';
    committed += convert(orderCommittedCost(order), currency, projectCurrency);
    const actual = convert(orderActualCost(order), currency, projectCurrency);
    if (actual <= 0) return;
    const linkedItems = order.packageId
      ? items.filter((item) => item.packageId && item.packageId === order.packageId)
      : [];
    const leafIds = linkedItems.flatMap((item) => descendantLeaves(item.id, byParent));
    if (leafIds.length > 0) {
      distributeByBac(actual, [...new Set(leafIds)], items).forEach((amount, itemId) => add(itemId, amount));
    } else {
      unassigned += actual;
    }
    sources.push({ type: 'oc', id: order.id, label: order.supplierName || order.id, amount: actual, date: order.createdDate });
  });

  const approvedStatements = paymentStatements.filter(
    (statement) => statement.parkId === parkId && ['aprobado', 'pagado'].includes(statement.status)
  );
  approvedStatements.forEach((statement) => {
    const contract = contractors.find((candidate) => candidate.id === statement.contractorId);
    const amount = Number(statement.grossAmount ?? statement.amount) || 0;
    if (amount <= 0) return;
    const leafIds = (contract?.wbsItemIds || []).flatMap((itemId) => descendantLeaves(itemId, byParent));
    if (leafIds.length > 0) {
      distributeByBac(amount, [...new Set(leafIds)], items).forEach((value, itemId) => add(itemId, value));
    } else {
      unassigned += amount;
    }
    sources.push({ type: 'ep', id: statement.id, label: `${contract?.name || 'Contratista'} · EP ${statement.number}`, amount, date: statement.periodEnd });
  });
  contractors
    .filter((contract) => contract.parkId === parkId)
    .forEach((contract) => {
      committed += Number(contract.contractAmount) || 0;
    });

  const total = [...byItem.values()].reduce((sum, value) => sum + value, 0) + unassigned;
  return { total, byItem, unassigned, committed, sources };
};

// ---------------------------------------------------------------------------
// Earned Value Management
// ---------------------------------------------------------------------------

export const evmIndicators = ({ bac = 0, pv = 0, ev = 0, ac = 0 }) => {
  const cpi = ratio(ev, ac);
  const spi = ratio(ev, pv);
  const eac = cpi && cpi > 0 ? bac / cpi : ac + Math.max(0, bac - ev);
  return {
    bac,
    pv,
    ev,
    ac,
    cv: ev - ac,
    sv: ev - pv,
    cpi,
    spi,
    eac,
    etc: Math.max(0, eac - ac),
    vac: bac - eac
  };
};

/**
 * EVM por partida (hojas y padres agregados) y del proyecto.
 * acByItem: Map itemId → costo real; acUnassigned se suma sólo al total.
 */
export const computeEvm = ({ items = [], logs = [], asOf, acByItem = new Map(), acUnassigned = 0 }) => {
  const byParent = childrenByParent(items);
  const leafRows = new Map();
  leafItems(items).forEach((leaf) => {
    const bac = Number(leaf.bac) || 0;
    const planned = plannedPctAt(leaf, asOf);
    const actual = actualPctAt(leaf.id, logs, asOf);
    leafRows.set(leaf.id, {
      planned,
      actual,
      ...evmIndicators({ bac, pv: (bac * planned) / 100, ev: (bac * actual) / 100, ac: acByItem.get(leaf.id) || 0 })
    });
  });

  const weights = effectiveWeights(items);
  const rows = flattenTree(items).map((item) => {
    const leafIds = descendantLeaves(item.id, byParent);
    const sums = leafIds.reduce(
      (acc, leafId) => {
        const row = leafRows.get(leafId);
        if (!row) return acc;
        const weight = weights.get(leafId) || 0;
        return {
          bac: acc.bac + row.bac,
          pv: acc.pv + row.pv,
          ev: acc.ev + row.ev,
          ac: acc.ac + row.ac,
          weight: acc.weight + weight,
          plannedW: acc.plannedW + weight * row.planned,
          actualW: acc.actualW + weight * row.actual
        };
      },
      { bac: 0, pv: 0, ev: 0, ac: 0, weight: 0, plannedW: 0, actualW: 0 }
    );
    const planned = sums.weight > 0 ? sums.plannedW / sums.weight : 0;
    const actual = sums.weight > 0 ? sums.actualW / sums.weight : 0;
    return {
      ...item,
      isLeaf: leafIds.length === 1 && leafIds[0] === item.id,
      weightPct: sums.weight * 100,
      planned,
      actual,
      progressSpi: ratio(actual, planned),
      ...evmIndicators({ bac: sums.bac, pv: sums.pv, ev: sums.ev, ac: sums.ac })
    };
  });

  const totals = [...leafRows.values()].reduce(
    (acc, row) => ({ bac: acc.bac + row.bac, pv: acc.pv + row.pv, ev: acc.ev + row.ev, ac: acc.ac + row.ac }),
    { bac: 0, pv: 0, ev: 0, ac: 0 }
  );
  const progress = projectProgressAt(items, logs, asOf);
  return {
    rows,
    project: {
      ...evmIndicators({ ...totals, ac: totals.ac + acUnassigned }),
      plannedProgress: progress.planned,
      actualProgress: progress.actual,
      progressSpi: progress.spi
    }
  };
};

// ---------------------------------------------------------------------------
// Hitos
// ---------------------------------------------------------------------------

export const milestoneStatus = (milestone, today = new Date(), soonDays = 14) => {
  if (milestone?.completedDate) return 'cumplido';
  const target = toDate(milestone?.targetDate);
  if (!target) return 'pendiente';
  const now = toDate(toIsoDate(today));
  if (target < now) return 'vencido';
  if ((target - now) / DAY_MS <= soonDays) return 'proximo';
  return 'pendiente';
};

// ---------------------------------------------------------------------------
// Estados de pago
// ---------------------------------------------------------------------------

// Valorización: (avance acumulado − avance ya pagado) × monto contrato, menos
// retención. Devuelve bruto, retención y neto.
export const paymentStatementAmounts = (contractAmount, progressPct, previousPct, retentionPct = 0) => {
  const delta = Math.max(0, clampPct(progressPct) - clampPct(previousPct));
  const gross = (Number(contractAmount) || 0) * (delta / 100);
  const retention = gross * ((Number(retentionPct) || 0) / 100);
  return { deltaPct: delta, grossAmount: gross, retention, netAmount: gross - retention };
};

// ---------------------------------------------------------------------------
// Conciliación de materiales: despachado → recibido → instalado
// ---------------------------------------------------------------------------

export const materialReconciliation = ({
  items = [],
  logs = [],
  shipments = [],
  consumptions = [],
  inventory = [],
  parkId,
  asOf,
  tolerancePct = 10
}) => {
  const rows = new Map();
  const ensure = (sku) => {
    if (!rows.has(sku)) {
      const product = inventory.find((item) => item.sku === sku);
      rows.set(sku, {
        sku,
        description: product?.description || product?.name || sku,
        unit: product?.unit || 'un',
        dispatched: 0,
        received: 0,
        installed: 0,
        planned: 0,
        expected: 0
      });
    }
    return rows.get(sku);
  };

  shipments
    .filter((shipment) => shipment.parkId === parkId)
    .forEach((shipment) => {
      (shipment.items || []).forEach((line) => {
        const row = ensure(line.sku);
        row.dispatched += Number(line.quantity) || 0;
        if (shipment.status === 'recepcionado') row.received += Number(line.quantity) || 0;
      });
    });

  consumptions
    .filter((entry) => entry.parkId === parkId)
    .forEach((entry) => {
      ensure(entry.sku).installed += Number(entry.quantity) || 0;
    });

  leafItems(items).forEach((leaf) => {
    const progress = actualPctAt(leaf.id, logs, asOf);
    (leaf.materials || []).forEach((material) => {
      const row = ensure(material.sku);
      const planned = Number(material.plannedQty) || 0;
      row.planned += planned;
      row.expected += (planned * progress) / 100;
    });
  });

  return [...rows.values()].map((row) => {
    const onSite = row.received - row.installed;
    const deviation = row.installed - row.expected;
    const base = row.planned > 0 ? row.planned : Math.max(row.expected, 1);
    const deviationPct = (deviation / base) * 100;
    let status = 'ok';
    if (row.installed > row.received) status = 'sobre_recibido';
    else if (row.planned > 0 && Math.abs(deviationPct) > tolerancePct) status = deviation > 0 ? 'sobreconsumo' : 'subconsumo';
    return { ...row, onSite, deviation, deviationPct, status };
  });
};

// ---------------------------------------------------------------------------
// Alertas de construcción (mismo formato que las alertas de Desarrollo)
// ---------------------------------------------------------------------------

export const ALERT_THRESHOLDS = { spiWarning: 1, spiCritical: 0.9, cpiWarning: 1, cpiCritical: 0.9 };

export const buildConstructionAlerts = ({
  parkId,
  projectName,
  evm,
  milestones = [],
  nonConformities = [],
  materials = [],
  today = new Date(),
  thresholds = ALERT_THRESHOLDS
}) => {
  const alerts = [];
  const todayDate = toDate(toIsoDate(today));
  const project = evm?.project;
  const daysBetween = (from) => Math.max(0, Math.floor((todayDate - toDate(from)) / DAY_MS));

  if (project && project.spi != null && project.spi < thresholds.spiWarning) {
    alerts.push({
      id: `${parkId}-spi`,
      parkId,
      type: 'spi',
      title: 'Atraso de obra (SPI < 1)',
      description: 'SPI {0} en {1}: avance real {2}% vs programado {3}%.',
      descriptionArgs: [project.spi.toFixed(2), projectName, project.actualProgress.toFixed(1), project.plannedProgress.toFixed(1)],
      priority: project.spi < thresholds.spiCritical ? 'alta' : 'media',
      overdueDays: 0
    });
  }
  if (project && project.cpi != null && project.cpi < thresholds.cpiWarning) {
    alerts.push({
      id: `${parkId}-cpi`,
      parkId,
      type: 'cpi',
      title: 'Sobrecosto de obra (CPI < 1)',
      description: 'CPI {0} en {1}: el costo real supera el valor ganado.',
      descriptionArgs: [project.cpi.toFixed(2), projectName],
      priority: project.cpi < thresholds.cpiCritical ? 'alta' : 'media',
      overdueDays: 0
    });
  }
  milestones
    .filter((milestone) => milestone.parkId === parkId && milestoneStatus(milestone, today) === 'vencido')
    .forEach((milestone) => {
      alerts.push({
        id: `${milestone.id}-vencido`,
        parkId,
        type: 'hito',
        title: 'Hito vencido: {0}',
        titleArgs: [milestone.name],
        description: 'Fecha objetivo {0} sin cumplimiento registrado.',
        descriptionArgs: [milestone.targetDate],
        dueDate: milestone.targetDate,
        priority: 'alta',
        overdueDays: daysBetween(milestone.targetDate)
      });
    });
  nonConformities
    .filter((nc) => nc.parkId === parkId && nc.status !== 'cerrada')
    .forEach((nc) => {
      const overdue = nc.dueDate && toDate(nc.dueDate) < todayDate;
      alerts.push({
        id: `${nc.id}-abierta`,
        parkId,
        type: 'nc',
        title: 'No conformidad abierta: {0}',
        titleArgs: [nc.title],
        description: nc.description || '',
        dueDate: nc.dueDate || null,
        responsible: nc.responsibleName || null,
        priority: nc.severity === 'alta' || overdue ? 'alta' : 'media',
        overdueDays: overdue ? daysBetween(nc.dueDate) : 0
      });
    });
  materials
    .filter((row) => row.status !== 'ok')
    .forEach((row) => {
      const labels = {
        sobre_recibido: 'Instalado supera lo recibido en obra',
        sobreconsumo: 'Consumo sobre lo esperado por avance',
        subconsumo: 'Consumo bajo lo esperado por avance'
      };
      alerts.push({
        id: `${parkId}-${row.sku}-material`,
        parkId,
        type: 'material',
        title: `${labels[row.status]}: {0}`,
        titleArgs: [row.sku],
        description: 'Instalado {0} vs esperado {1} ({2}%).',
        descriptionArgs: [row.installed, row.expected.toFixed(1), row.deviationPct.toFixed(0)],
        priority: row.status === 'sobre_recibido' ? 'alta' : 'media',
        overdueDays: 0
      });
    });
  return alerts;
};
